// ==========================================================================
// screens/Bills.tsx — bills to pay and reminders (FF-056, 10 Oct 2026)
//   James: "load in a bill with a due date it records it as payment pending
//   till you reconcile it or flag it as paid, then it reconciles when the bank
//   csv is loaded. Reminders just handy to have."
// ==========================================================================
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { BellRing, Check, FileText, Link2, Paperclip, Plus, ReceiptText, Trash2 } from "lucide-react";
import ChapterHeader from "@/components/ChapterHeader";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import ContactPicker from "@/components/ContactPicker";
import { CodePicker } from "@/screens/MoneyTransactions";
import { tokenColour, ymd } from "@/lib/calendar";
import { useContacts } from "@/lib/contacts";
import { usePeople } from "@/lib/household";
import { fromCents, money, toCents, useCategories } from "@/lib/money";
import { billPending, useAddBill, useReceipts, useSaveReceipt, type Receipt } from "@/lib/receipts";
import { EVERY, KINDS, daysTo, dueText, kindOf, useDoneReminder, useReminders, useSaveReminder, type Reminder, type ReminderDraft } from "@/lib/reminders";
import { useVehicles } from "@/lib/vehicles";
import { useProperties } from "@/lib/property";

const tone = (iso: string | null | undefined, warn = 7) => { if (!iso) return "text-ink-muted"; const n = daysTo(iso); return n < 0 ? "text-bad" : n <= warn ? "text-warn" : "text-ink-muted"; };

export default function Bills() {
  const receipts = useReceipts(); const reminders = useReminders(); const contacts = useContacts();
  const markPaid = useSaveReceipt(); const done = useDoneReminder();
  const [addBill, setAddBill] = useState(false);
  const [rem, setRem] = useState<ReminderDraft | null>(null);
  const all = receipts.data ?? [];
  const toPay = all.filter(billPending).sort((a, b) => (a.due_on ?? "9999").localeCompare(b.due_on ?? "9999"));
  const paid = all.filter((r) => r.is_bill && !r.archived_at && (r.transaction_id || r.paid_on)).slice(0, 8);
  const owing = toPay.reduce((t, r) => t + (r.total_cents ?? 0), 0);
  const overdue = toPay.filter((r) => r.due_on && daysTo(r.due_on) < 0).length;
  const live = (reminders.data ?? []).filter((r) => !r.done_on);
  const soon = live.filter((r) => daysTo(r.due_on) <= r.warn_days);
  const later = live.filter((r) => daysTo(r.due_on) > r.warn_days);
  const who = (r: Receipt) => (contacts.data ?? []).find((c) => c.id === r.contact_id)?.name ?? r.note ?? r.file_name ?? "A bill";
  const newRem = (): ReminderDraft => ({ title: "", kind: "rego", due_on: ymd(new Date()), every: "year", warn_days: 14, asset_id: null, person_id: null, amount_cents: null, note: null });

  return (
    <>
      <ChapterHeader chapter="bills" title="Bills and reminders" subtitle="What needs paying, and what's coming up: rego, insurance, passports, renewals."
        action={<div className="flex gap-2">
          <button type="button" onClick={() => setRem(newRem())} className="flex items-center gap-1.5 rounded-full bg-surface px-4 py-2 text-sm font-semibold shadow-soft ring-1 ring-line/70 hover:ring-primary/40"><BellRing className="h-4 w-4" />Add a reminder</button>
          <button type="button" onClick={() => setAddBill(true)} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-ink shadow-soft"><Plus className="h-4 w-4" />Add a bill</button>
        </div>} />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <Stat big value={money(owing)} label={`to pay across ${toPay.length} bill${toPay.length === 1 ? "" : "s"}`} colour="primary" />
        <Stat value={String(overdue)} label="overdue" colour={overdue ? "danger" : "success"} />
        <Stat value={String(soon.length)} label="reminders coming up" colour="chart-4" />
      </div>

      <div className="grid gap-8 xl:grid-cols-2">
        <section className="min-w-0">
          <h2 className="mb-3 text-[17px] font-semibold">Bills to pay</h2>
          {toPay.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-line p-8 text-center text-sm text-ink-muted">
              <ReceiptText className="mx-auto mb-2 h-8 w-8" />Nothing to pay. Add a bill when one comes in, or flag a receipt as a bill in Money → Receipts.
            </div>
          ) : (
            <ul className="space-y-2">
              {toPay.map((r) => {
                const n = r.due_on ? differenceInCalendarDays(parseISO(r.due_on), new Date()) : null;
                return (
                  <li key={r.id} className="flex items-center gap-3 rounded-xl bg-surface p-3 shadow-soft ring-1 ring-line/70">
                    <div className={`flex w-14 shrink-0 flex-col items-center rounded-lg py-1 ring-1 ${n !== null && n < 0 ? "bg-bad/10 ring-bad/30" : n !== null && n <= 7 ? "bg-warn/10 ring-warn/30" : "bg-surface-raised ring-line"}`}>
                      <span className="text-[11px] font-semibold uppercase text-ink-muted">{r.due_on ? format(parseISO(r.due_on), "MMM") : "Due"}</span>
                      <span className="font-heading text-xl font-semibold leading-tight">{r.due_on ? format(parseISO(r.due_on), "d") : "?"}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{who(r)}</div>
                      <div className={`text-xs ${tone(r.due_on)}`}>{r.due_on ? dueText(r.due_on) : "No due date"}{r.uploaded ? <Paperclip className="ml-1.5 inline h-3 w-3 text-ink-muted" /> : null}</div>
                    </div>
                    <div className="num text-lg font-semibold">{r.total_cents ? money(r.total_cents, { cents: true }) : "—"}</div>
                    <button type="button" onClick={() => markPaid.mutate({ id: r.id, r: { paid_on: ymd(new Date()) } })} className="flex shrink-0 items-center gap-1 rounded-full bg-good/15 px-3 py-1.5 text-xs font-semibold text-good hover:bg-good/25"><Check className="h-3.5 w-3.5" />Paid</button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-3 text-xs text-ink-muted">When the bank CSV comes in, Reconcile matches each bill to its payment (same amount, between the bill date and a week after it's due). "Paid" is for when you just want it off the list.</p>

          {paid.length > 0 && (
            <>
              <h3 className="mb-2 mt-8 text-sm font-semibold text-ink-muted">Paid lately</h3>
              <ul className="divide-y divide-line/60 rounded-xl bg-surface ring-1 ring-line/70">
                {paid.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="min-w-0 flex-1 truncate">{who(r)}</span>
                    <span className="num">{r.total_cents ? money(r.total_cents, { cents: true }) : ""}</span>
                    {r.transaction_id ? <span className="flex items-center gap-1 text-xs text-good"><Link2 className="h-3.5 w-3.5" />matched</span>
                      : <button type="button" onClick={() => markPaid.mutate({ id: r.id, r: { paid_on: null } })} className="text-xs text-ink-muted hover:text-ink">marked paid {r.paid_on ? format(parseISO(r.paid_on), "d MMM") : ""} · undo</button>}
                  </li>
                ))}
              </ul>
            </>
          )}
          <Link to="/money/receipts" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"><FileText className="h-4 w-4" />All receipts and bills</Link>
        </section>

        <section className="min-w-0">
          <h2 className="mb-3 text-[17px] font-semibold">Reminders</h2>
          {live.length === 0 && (
            <button type="button" onClick={() => setRem(newRem())} className="w-full rounded-2xl border-2 border-dashed border-line p-8 text-center text-sm text-ink-muted hover:text-ink">
              <BellRing className="mx-auto mb-2 h-8 w-8" />Add the Prado's rego, the home insurance renewal, when the passports run out. Each one can repeat.
            </button>
          )}
          {soon.length > 0 && <ReminderList title="Coming up" list={soon} onOpen={(r) => setRem({ ...r })} onDone={(r) => done.mutate(r)} />}
          {later.length > 0 && <ReminderList title="Later" list={later} onOpen={(r) => setRem({ ...r })} onDone={(r) => done.mutate(r)} quiet />}
        </section>
      </div>

      {addBill && <BillSheet onClose={() => setAddBill(false)} />}
      {rem && <ReminderSheet d={rem} onClose={() => setRem(null)} />}
    </>
  );
}

function Stat({ value, label, colour, big }: { value: string; label: string; colour: string; big?: boolean }) {
  return (
    <div className="rounded-2xl bg-surface p-5 shadow-soft ring-1 ring-line/70">
      <div className={`num font-semibold ${big ? "text-[34px]" : "text-3xl"}`} style={{ color: tokenColour(colour) }}>{value}</div>
      <div className="text-sm text-ink-muted">{label}</div>
    </div>
  );
}

function ReminderList({ title, list, onOpen, onDone, quiet }: { title: string; list: Reminder[]; onOpen: (r: Reminder) => void; onDone: (r: Reminder) => void; quiet?: boolean }) {
  const vehicles = useVehicles(); const people = usePeople(); const props = useProperties();
  const assetName = (id: string | null) => id ? vehicles.data?.find((v) => v.asset_id === id)?.assets.name ?? props.data?.props.find((p) => p.asset_id === id)?.assets.name : undefined;
  return (
    <div className="mb-6">
      <h3 className="mb-2 text-sm font-semibold text-ink-muted">{title}</h3>
      <ul className="space-y-2">
        {list.map((r) => {
          const k = kindOf(r.kind); const forWhat = assetName(r.asset_id) ?? people.data?.find((p) => p.id === r.person_id)?.display_name;
          return (
            <li key={r.id} className={`flex items-center gap-3 rounded-xl p-3 ring-1 ring-line/70 ${quiet ? "bg-surface/60" : "bg-surface shadow-soft"}`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-raised text-xl">{k.emoji}</span>
              <button type="button" onClick={() => onOpen(r)} className="min-w-0 flex-1 text-left">
                <span className="block truncate font-semibold">{r.title}</span>
                <span className="block truncate text-xs"><span className={tone(r.due_on, r.warn_days)}>{dueText(r.due_on)}</span><span className="text-ink-muted">{[forWhat, r.every !== "none" ? EVERY.find((e) => e.key === r.every)?.label.toLowerCase() : null, r.amount_cents ? `about ${money(r.amount_cents)}` : null].filter(Boolean).map((x) => `, ${x}`).join("")}</span></span>
              </button>
              <button type="button" onClick={() => onDone(r)} title={r.every === "none" ? "Done" : "Done: move to the next one"} className="flex shrink-0 items-center gap-1 rounded-full bg-surface-raised px-3 py-1.5 text-xs font-semibold hover:bg-good/15 hover:text-good"><Check className="h-3.5 w-3.5" />Done</button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BillSheet({ onClose }: { onClose: () => void }) {
  const add = useAddBill(); const contacts = useContacts(); const cats = useCategories();
  const [file, setFile] = useState<File | undefined>(); const pick = useRef<HTMLInputElement>(null);
  const [who, setWho] = useState<string | null>(null); const [total, setTotal] = useState("");
  const [billDate, setBillDate] = useState(ymd(new Date())); const [due, setDue] = useState("");
  const [cat, setCat] = useState(""); const [note, setNote] = useState(""); const [err, setErr] = useState<string | null>(null);
  const submit = () => {
    if (!due) { setErr("When is it due?"); return; }
    const cents = toCents(total);
    if (!cents) { setErr("How much is it?"); return; }
    add.mutate({ file, contact_id: who, total_cents: cents, receipt_date: billDate || null, due_on: due, category_id: cat || null, note: note.trim() || null }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title="Add a bill" onClose={onClose}>
      <div className="space-y-4">
        <input ref={pick} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { setFile(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" onClick={() => pick.current?.click()} className="flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-line p-4 text-left text-sm hover:border-primary/50">
          <Paperclip className="h-5 w-5 shrink-0 text-ink-muted" />
          <span className="min-w-0 flex-1 truncate">{file ? file.name : <><b>Attach the bill</b> <span className="text-ink-muted">(PDF or photo, optional)</span></>}</span>
        </button>
        <Lbl label="Who it's from"><ContactPicker value={who} contacts={contacts.data ?? []} onChange={(id) => setWho(id)} newName="" /></Lbl>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Amount"><input className={inputCls} inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="$" /></Lbl>
          <Lbl label="Due"><input type="date" className={inputCls} value={due} onChange={(e) => setDue(e.target.value)} /></Lbl>
        </div>
        <Lbl label="Bill date"><input type="date" className={inputCls} value={billDate} onChange={(e) => setBillDate(e.target.value)} /></Lbl>
        <Lbl label="Budget code (optional)"><CodePicker key={cat || "none"} value={cat} cats={cats.data ?? []} sign={-1} onChange={setCat} startOpen={false} /></Lbl>
        <Lbl label="Note (optional)"><input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Council rates Q2, Origin electricity…" /></Lbl>
        <ErrorText>{err}</ErrorText>
        <button type="button" onClick={submit} disabled={add.isPending} className="w-full rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">{add.isPending ? "Saving…" : "Add to bills to pay"}</button>
      </div>
    </Sheet>
  );
}

function ReminderSheet({ d, onClose }: { d: ReminderDraft; onClose: () => void }) {
  const save = useSaveReminder(); const vehicles = useVehicles(); const props = useProperties(); const people = usePeople();
  const [f, setF] = useState(d); const set = (p: Partial<ReminderDraft>) => setF({ ...f, ...p });
  const [amount, setAmount] = useState(fromCents(d.amount_cents)); const [err, setErr] = useState<string | null>(null);
  const assets = useMemo(() => [
    ...(vehicles.data ?? []).filter((v) => !v.assets.disposed_on).map((v) => ({ id: v.asset_id, name: v.assets.name })),
    ...(props.data?.props ?? []).filter((p) => !p.assets.disposed_on).map((p) => ({ id: p.asset_id, name: p.assets.name })),
  ], [vehicles.data, props.data]);
  const submit = () => {
    if (!f.title.trim()) { setErr("What's it for?"); return; }
    save.mutate({ ...f, amount_cents: amount.trim() ? toCents(amount) : null }, { onSuccess: onClose, onError: (e) => setErr(e.message) });
  };
  return (
    <Sheet title={d.id ? "Reminder" : "Add a reminder"} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-5 gap-1.5">
          {KINDS.map((k) => (
            <button key={k.key} type="button" onClick={() => set({ kind: k.key, every: ["rego", "insurance", "subscription", "service", "tax"].includes(k.key) ? "year" : f.every })}
              className={`flex flex-col items-center gap-0.5 rounded-xl py-2 text-[11px] ${f.kind === k.key ? "bg-primary font-semibold text-white" : "bg-surface-raised"}`}><span className="text-xl">{k.emoji}</span>{k.label}</button>
          ))}
        </div>
        <Lbl label="What"><input className={inputCls} value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder={f.kind === "rego" ? "Prado rego" : f.kind === "passport" ? "James's passport" : "Home and contents insurance"} autoFocus /></Lbl>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Due"><input type="date" className={inputCls} value={f.due_on} onChange={(e) => set({ due_on: e.target.value })} /></Lbl>
          <Lbl label="Repeats"><select className={inputCls} value={f.every} onChange={(e) => set({ every: e.target.value as ReminderDraft["every"] })}>{EVERY.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}</select></Lbl>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="Warn me"><select className={inputCls} value={f.warn_days} onChange={(e) => set({ warn_days: Number(e.target.value) })}>{[3, 7, 14, 30, 60, 90].map((n) => <option key={n} value={n}>{n} days before</option>)}</select></Lbl>
          <Lbl label="About (optional)"><input className={inputCls} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$" /></Lbl>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="For (optional)"><select className={inputCls} value={f.asset_id ?? ""} onChange={(e) => set({ asset_id: e.target.value || null })}><option value="">Nothing in particular</option>{assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Lbl>
          <Lbl label="Whose (optional)"><select className={inputCls} value={f.person_id ?? ""} onChange={(e) => set({ person_id: e.target.value || null })}><option value="">The family</option>{(people.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}</select></Lbl>
        </div>
        <Lbl label="Note (optional)"><input className={inputCls} value={f.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="Policy with…, renew online at…" /></Lbl>
        <ErrorText>{err}</ErrorText>
        <div className="flex gap-2">
          <button type="button" onClick={submit} disabled={save.isPending} className="flex-1 rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">Save</button>
          {d.id && <button type="button" onClick={() => save.mutate({ ...f, archive: true }, { onSuccess: onClose })} className="flex items-center gap-1.5 rounded-full bg-surface-raised px-4 py-3 text-sm font-semibold text-bad"><Trash2 className="h-4 w-4" />Remove</button>}
        </div>
      </div>
    </Sheet>
  );
}
